/**
 * Manually curated top results for high-traffic search terms.
 *
 * Popular queries like "beach" hit a wall of themes that all score a clean
 * 1.000 concept similarity (we annotated dozens of themes with the same
 * keyword), so the automatic tiebreaks are left arbitrating between rows that
 * are genuinely indistinguishable to the scorer. This map lets us hand-pick
 * which themes lead — and in what order — for those terms.
 *
 * Keys are the lowercased, trimmed search term. Values are entries, best-first
 * (the entry listed first ranks first), in the `printGalleryThemeDisplayOrder`
 * flag's syntax minus its orientation prefix:
 *
 *     themeId[+colorwayId]
 *
 * e.g. `beach_arch` or `beach_arch+sand`. A bare themeId covers every colorway
 * of the theme; `+colorwayId` curates a single colorway.
 *
 * The flag's `tall `/`wide ` prefix is deliberately *not* supported here.
 * Orientation is a property of a template's dimensions, which search never sees
 * — rows are scored per (theme × colorway) against the embeddings index, with no
 * width/height in hand — so an orientation-prefixed entry could only ever fail
 * to match. Curate the theme and let the existing per-template grouping place
 * the orientation variants.
 *
 * Curation supports *literal* keyword matches only. A term earns a 1.000 score
 * by being the exact keyword a theme was annotated with — cosine of a vector
 * with itself — which is why popular terms pile up at a perfect tie in the
 * first place. Synonyms take the semantic path and land well short: "playa"
 * scores ~0.683 against "beach", so a `playa` entry here would be dead code no
 * matter which themes it lists. To curate a synonym, annotate the themes with
 * that keyword instead.
 */
export const CURATED_THEMES_BY_SEARCH_TERM: ReadonlyMap<string, ReadonlyArray<string>> = new Map([['beach', ['sea_breeze', 'tropicana+ocean']]]);

/**
 * Similarity at or above which a row counts as a "clean match" eligible for
 * curation. Concept cosines top out at 1, but accumulated float math (and the
 * 1.01 lexical boost) means an exact hit can land a hair off, so compare with a
 * tolerance rather than `=== 1`.
 */
const PERFECT_SIMILARITY = 1 - 1e-6;

export const isPerfectSimilarity = (similarity: number): boolean => similarity >= PERFECT_SIMILARITY;

/** Identity of a scored row, matched against a parsed curated entry. `colorwayId` is absent for colorway-less rows. */
export type CuratedRowIdentity = Readonly<{
  themeId: string;
  colorwayId?: string;
}>;

/**
 * Splits a curated entry into theme and colorway.
 *
 * The `+` delimiter is explicit on purpose, and is why this shares the flag's
 * grammar rather than inventing one: themeIds themselves contain `_`
 * (`modern_lines`, `upload_your_design`) and some color-suffixed strings are real
 * legacy themeIds (`emilio_sage`), so `_` cannot be parsed unambiguously.
 */
const parseCuratedEntry = (entry: string): { themeId: string; colorwayId?: string } => {
  const plusIndex = entry.indexOf('+');
  const themeId = (plusIndex === -1 ? entry : entry.slice(0, plusIndex)).trim();
  const colorwayId = plusIndex === -1 ? undefined : entry.slice(plusIndex + 1).trim() || undefined;
  return { themeId, colorwayId };
};

const matchesEntry = (entry: string, row: CuratedRowIdentity): boolean => {
  const { themeId, colorwayId } = parseCuratedEntry(entry);
  if (themeId !== row.themeId) return false;
  // A bare themeId covers every colorway of the theme; `+colorwayId` narrows to one.
  return !colorwayId || colorwayId === row.colorwayId;
};

/**
 * Rank of `row` in the curated list for `searchTerm`, or
 * `Number.POSITIVE_INFINITY` when it isn't curated — so uncurated rows sort
 * after every curated one and stay tied with each other, deferring to the lower
 * precedence rules.
 *
 * When several entries match a row (e.g. a bare `boldly` and a narrower
 * `boldly+tomato`), the earliest wins: a row ranks by the best position it was
 * curated into.
 */
export function getCuratedRank(searchTerm: string | undefined, row: CuratedRowIdentity): number {
  if (!searchTerm) return Number.POSITIVE_INFINITY;
  const curated = CURATED_THEMES_BY_SEARCH_TERM.get(searchTerm.trim().toLowerCase());
  if (!curated) return Number.POSITIVE_INFINITY;
  const index = curated.findIndex(entry => matchesEntry(entry, row));
  return index === -1 ? Number.POSITIVE_INFINITY : index;
}
